/**
 * usePlaytestSocket — Socket-Verbindung eines Playtests und alle Aktionen des Bretts.
 *
 * Eigene Aktionen, die der Nutzer sofort sehen soll, laufen als Vormerkung (siehe
 * `context/PlaytestContext.js`): `emit` erzeugt eine `requestId`, wendet die Änderung mit ihrer
 * Umkehr-Aktion an und sendet. Der Server schickt das Ereignis an den Auslöser mit derselben
 * `requestId` zurück (→ `OPTIMISTIC_CONFIRM` mit den Aktionen des Ereignisses) oder
 * `playtest:error` mit ihr (→
 * `OPTIMISTIC_REVERT` und Toast). Bleibt eine Vormerkung länger als `PENDING_TIMEOUT_MS`
 * unbeantwortet, holt der Hook einmal den vollen Zustand nach, der alle Vormerkungen ablöst.
 *
 * Hinweis-Pings (`playtest:card:pinged`) landen in `state.pings` und verfallen nach
 * `PING_VISIBLE_MS`; der Ping eines anderen erscheint zusätzlich als Toast, dessen Text die Seite
 * über die Option `onFocusCard(instanceId)` zur Karte springen lässt. Weitere Toasts, alles andere
 * steht nur im Protokoll: „Du bist dran" beim Zugwechsel auf den eigenen Nutzer, die Übernahme
 * einer eigenen Karte durch einen anderen und die Ablehnung einer eigenen Hand- oder
 * Kontroll-Anfrage.
 *
 * Kommt `playtest:card:drawn` für den eigenen Nutzer ohne Kartendaten (ein zweiter Tab hat
 * gezogen), fordert der Hook den vollen Stand an — die Hand dieses Tabs kennt die Karten sonst nie.
 *
 * Rückgabe: `connected`, `reconnecting` (Verbindung verloren, Socket.io versucht es erneut),
 * `reconnectFailed` (alle Versuche gescheitert; `reconnect()` startet neu), `error` (Text eines
 * Problems beim ersten Verbindungsaufbau, sonst `null`), `syncedOnce` und die Aktionen. Fehler einzelner
 * Aktionen erscheinen als Toast, nicht in `error`. Ist der Playtest für diesen Nutzer nicht (mehr)
 * erreichbar — Session beendet oder unbekannt, kein Teilnehmer, Anmeldung abgelaufen —, setzt der
 * Hook `state.boardClosed { message, code }` mit dem anzuzeigenden Text und meldet nichts
 * zusätzlich in `error`; die Seite verlässt darauf das Brett.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../../../context/AuthContext';
import { usePlaytest, PING_VISIBLE_MS } from '../../../context/PlaytestContext';
import { ApiClientError } from '../../../api/ApiClientError';
import { errorMessage } from '../../../api/errorMessage';
import { useToast } from '../../../ui/Toast';

export const PENDING_TIMEOUT_MS = 10_000;
const PENDING_CHECK_INTERVAL_MS = 2_000;

/** Fehler, nach denen das Brett für diesen Nutzer nicht weiterlaufen kann. */
const FATAL_ERROR_CODES = new Set(['SESSION_NOT_FOUND', 'SESSION_INACTIVE', 'NOT_SESSION_MEMBER']);

function createRequestId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sameUser(a, b) {
    return a === b || String(a) === String(b);
}

function playerOf(state, userId) {
    return (state?.players || []).find(p => sameUser(p.userId, userId));
}

/** Stapelplatz über allen Brettkarten, wie ihn der Server einer neu ausgelegten Karte gibt. */
function nextZIndex(state) {
    const zIndexes = (state?.players || []).flatMap(p => p.battlefield || []).filter(Boolean).map(c => c.zIndex ?? 0);
    return Math.max(0, ...zIndexes) + 1;
}

/** Neue Oberkarte einer offen gespielten Bibliothek, die ein Ereignis mitträgt. */
function deckTopActions(deckTop) {
    return deckTop ? [{ type: 'DECK_TOP_REVEALED', payload: { playerId: deckTop.userId, topCard: deckTop.card } }] : [];
}

function findIn(player, zones, instanceId) {
    for (const zone of zones) {
        const cards = player?.[zone];
        if (!Array.isArray(cards)) continue;
        const index = cards.findIndex(c => c && c.instanceId === instanceId);
        if (index !== -1) return { card: cards[index], zone, index };
    }
    return null;
}

/**
 * Server-Ereignis → Reducer-Aktion(en). Ein Ereignis mit `requestId` geht als
 * `OPTIMISTIC_CONFIRM` an den Reducer, der damit zugleich die Vormerkung des Auslösers auflöst.
 */
const SERVER_EVENTS = {
    'playtest:player:joined': ({ userId }) => ({ type: 'PLAYER_PRESENCE_CHANGED', payload: { userId, isAway: false } }),
    'playtest:player:reconnected': ({ userId }) => ({ type: 'PLAYER_PRESENCE_CHANGED', payload: { userId, isAway: false } }),
    'playtest:player:disconnected': ({ userId }) => ({ type: 'PLAYER_PRESENCE_CHANGED', payload: { userId, isAway: true } }),

    'playtest:card:drawn': ({ userId, count, cardsData, deckRemaining, isMulliganDraw, mulliganCount, cardsToBottomCount, deckTop }) => {
        if (isMulliganDraw && Array.isArray(cardsData)) {
            // Mulligan: die Hand wird ersetzt, nicht ergänzt
            return { type: 'MULLIGAN_TAKEN', payload: { userId, mulliganCount: mulliganCount ?? 0, cardsToBottomCount: cardsToBottomCount ?? 0, newHand: cardsData, deckRemaining } };
        }
        if (Array.isArray(cardsData) && cardsData.length > 0) {
            return { type: 'CARDS_DRAWN', payload: { playerId: userId, cards: cardsData, deckRemaining } };
        }
        return [{ type: 'OPPONENT_DRAWN', payload: { playerId: userId, count } }, ...deckTopActions(deckTop)];
    },
    'playtest:card:played': ({ userId, card }) => (card?.instanceId
        ? { type: 'CARD_PLAYED', payload: { playerId: userId, instanceId: card.instanceId, card } }
        : null),
    'playtest:card:zoneMoved': ({ userId, instanceId, fromZone, toZone, card, insertAtTop, targetPlayerId, deckTop }) => [
        { type: 'CARD_MOVED', payload: { playerId: userId, targetPlayerId: targetPlayerId || userId, instanceId, fromZone, toZone, card, insertAtTop } },
        ...deckTopActions(deckTop),
    ],
    'playtest:card:copied': ({ userId, card }) => (
        { type: 'CARD_MOVED', payload: { playerId: userId, instanceId: card.instanceId, fromZone: null, toZone: 'battlefield', card } }
    ),
    'playtest:card:tapped': ({ userId, instanceId, tapped }) => ({ type: 'CARD_TAPPED', payload: { playerId: userId, instanceId, tapped } }),
    'playtest:card:allUntapped': ({ userId, instanceIds }) => ({ type: 'ALL_UNTAPPED', payload: { playerId: userId, instanceIds } }),
    'playtest:card:transformed': ({ userId, instanceId, transformedFace, counters }) => (
        { type: 'CARD_TRANSFORMED', payload: { playerId: userId, instanceId, transformedFace, counters } }
    ),
    'playtest:card:faceDownChanged': ({ userId, instanceId, faceDown, card }) => (
        { type: 'CARD_FACEDOWN_CHANGED', payload: { playerId: userId, instanceId, faceDown, card } }
    ),
    'playtest:card:positionChanged': ({ userId, instanceId, position, zIndex }) => (
        { type: 'CARD_POSITION_UPDATED', payload: { playerId: userId, instanceId, position, zIndex } }
    ),
    'playtest:card:counterChanged': ({ userId, instanceId, counterType, newValue }) => (
        { type: 'COUNTERS_UPDATED', payload: { playerId: userId, instanceId, counterType, newValue } }
    ),
    'playtest:card:markerChanged': ({ userId, instanceId, markerType, enabled, markers }) => (
        { type: 'MARKERS_UPDATED', payload: { playerId: userId, instanceId, markerType, enabled, markers } }
    ),
    'playtest:token:created': ({ userId, token }) => ({ type: 'TOKEN_CREATED', payload: { playerId: userId, token } }),
    'playtest:token:addedToAvailable': ({ userId, token }) => ({ type: 'TOKEN_ADDED_TO_AVAILABLE', payload: { playerId: userId, token } }),
    // Die neue Reihenfolge der Bibliothek kommt über den nachgeholten Zustand (RESYNC_AFTER_EVENTS).
    'playtest:zone:shuffled': () => null,
    'playtest:deck:openPlayChanged': ({ userId, deckOpenPlay, topCard }) => (
        { type: 'DECK_OPEN_PLAY_CHANGED', payload: { playerId: userId, deckOpenPlay, topCard } }
    ),

    'playtest:player:lifeChanged': ({ userId, newLife, eliminated }) => ({ type: 'LIFE_UPDATED', payload: { playerId: userId, newLife, eliminated } }),
    'playtest:player:commanderDamageChanged': ({ targetUserId, newCommanderDamage, newLife }) => (
        { type: 'COMMANDER_DAMAGE_UPDATED', payload: { targetUserId, newCommanderDamage, newLife } }
    ),
    'playtest:player:counterChanged': ({ userId, counters }) => ({ type: 'PLAYER_COUNTERS_CHANGED', payload: { userId, counters } }),
    'playtest:player:eliminated': ({ userId }) => ({ type: 'PLAYER_ELIMINATED', payload: { userId } }),
    'playtest:dice:rolled': ({ userId, displayName, sides, result }) => ({ type: 'DICE_ROLLED', payload: { userId, displayName, sides, result } }),
    'playtest:notification': ({ message, timestamp }) => (
        { type: 'LOG_ENTRY_ADDED', payload: { message, timestamp: timestamp ?? Date.now() } }
    ),

    'playtest:hand:viewRequest': ({ requesterId, requesterName }) => ({ type: 'HAND_VIEW_REQUESTED', payload: { requesterId, requesterName } }),
    'playtest:hand:revealed': ({ targetUserId, targetName, cards }) => ({ type: 'HAND_REVEALED', payload: { targetUserId, targetName, cards } }),
    'playtest:card:controlRequest': ({ requesterId, requesterName, instanceId, cardName }) => (
        { type: 'CONTROL_REQUESTED', payload: { requesterId, requesterName, instanceId, cardName } }
    ),
    'playtest:card:controlChanged': ({ ownerId, controllerId, instanceId, card }) => (
        { type: 'CARD_CONTROL_CHANGED', payload: { ownerId, controllerId, instanceId, card } }
    ),
    'playtest:card:controlReturned': ({ ownerId, controllerId, instanceId, card }) => (
        { type: 'CARD_CONTROL_RETURNED', payload: { ownerId, controllerId, instanceId, card } }
    ),

    'playtest:mulligan:playerTookMulligan': ({ userId, mulliganCount, cardsToBottomCount }) => (
        { type: 'MULLIGAN_TAKEN', payload: { userId, mulliganCount, cardsToBottomCount } }
    ),
    'playtest:mulligan:handKept': ({ hand, deckRemaining }) => ({ type: 'MULLIGAN_HAND_KEPT', payload: { hand, deckRemaining } }),
    'playtest:mulligan:playerKept': ({ userId, mulliganCount, cardsToBottomCount }) => (
        { type: 'MULLIGAN_PLAYER_KEPT', payload: { userId, mulliganCount, cardsToBottomCount } }
    ),
    'playtest:mulligan:phaseEnded': ({ currentTurnUserId, turnNumber }) => ({ type: 'MULLIGAN_PHASE_ENDED', payload: { currentTurnUserId, turnNumber } }),
    'playtest:turn:changed': ({ previousUserId, newUserId, turnNumber }) => ({ type: 'TURN_CHANGED', payload: { previousUserId, newUserId, turnNumber } }),
    'playtest:session:ended': ({ message }) => ({ type: 'BOARD_CLOSED', payload: { message: message || 'Session vom Host beendet', code: 'SESSION_ENDED' } }),
};

const nameOf = (state, userId) => playerOf(state, userId)?.displayName || `Teilnehmer ${userId}`;

/**
 * Ereignisse, die zusätzlich einen Toast zeigen: `(data, state) => Text oder null`. `state` ist der
 * Stand vor dem Ereignis. Die Ablehnungen ändern keinen Zustand und haben deshalb nur den Toast.
 */
const EVENT_TOASTS = {
    // Geht der Zug an den zurück, der ihn gerade beendet hat (allein oder alle anderen weg), braucht
    // er keinen Hinweis.
    'playtest:turn:changed': ({ previousUserId, newUserId }, state) => (
        newUserId != null && sameUser(newUserId, state?.currentUserId) && !sameUser(previousUserId, newUserId)
            ? 'Du bist dran'
            : null
    ),
    'playtest:card:controlChanged': ({ ownerId, controllerId, card }, state) => {
        if (!sameUser(ownerId, state?.currentUserId) || sameUser(controllerId, state?.currentUserId)) return null;
        const cardName = card?.faceDown ? 'deine verdeckte Karte' : (card?.scryfallData?.name || 'deine Karte');
        return `${nameOf(state, controllerId)} übernimmt die Kontrolle über ${cardName}`;
    },
    'playtest:hand:viewDenied': ({ targetName }) => `${targetName || 'Der Teilnehmer'} zeigt seine Hand nicht`,
    'playtest:card:controlDenied': ({ targetName }) => `${targetName || 'Der Teilnehmer'} gibt die Kontrolle nicht ab`,
};

/**
 * Nach diesen Ereignissen kennt der Client die Reihenfolge der Bibliothek nicht mehr (Mischen,
 * Standardland holen, Karten unter die Bibliothek legen); der volle Zustand bringt sie zurück.
 */
const RESYNC_AFTER_EVENTS = new Set(['playtest:zone:shuffled', 'playtest:mulligan:handKept']);

/**
 * @param {number|string} sessionId - Playtest-Session
 * @param {{ onFocusCard?: (instanceId: string) => void }} [options] - `onFocusCard` richtet die
 *   Kamera auf eine Brettkarte; ohne ihn ist der Ping-Toast nicht klickbar.
 */
export function usePlaytestSocket(sessionId, { onFocusCard } = {}) {
    const socketRef = useRef(null);
    const onFocusCardRef = useRef(onFocusCard);
    const connectedRef = useRef(false);
    // Nach der ersten Verbindung melden Offline-Marker und Wiederverbinden-Modal das Problem; ein
    // Fehlertext je gescheitertem Versuch käme als Toast-Serie dazu.
    const everConnectedRef = useRef(false);
    const stateRef = useRef(null);
    const resyncedRequestIds = useRef(new Set());
    const { authToken } = useAuth();
    const { state, dispatch } = usePlaytest();
    const toast = useToast();
    const [connected, setConnected] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const [reconnectFailed, setReconnectFailed] = useState(false);
    const [error, setError] = useState(null);
    const [syncedOnce, setSyncedOnce] = useState(false);
    const sessionIdNum = parseInt(sessionId, 10);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        onFocusCardRef.current = onFocusCard;
    }, [onFocusCard]);

    // ============================================================
    // VERBINDUNG UND SERVER-EREIGNISSE
    // ============================================================

    useEffect(() => {
        if (!authToken || !sessionIdNum) {
            return;
        }

        // Produktion: derselbe Host, Caddy leitet socket.io an das Backend weiter.
        const socketUrl = process.env.NODE_ENV === 'production'
            ? window.location.origin
            : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001');

        const socket = io(socketUrl, {
            auth: { token: authToken },
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5,
        });
        socketRef.current = socket;

        const setConnectedBoth = (value) => {
            connectedRef.current = value;
            setConnected(value);
        };

        // Auch nach jedem automatischen Wiederverbinden: der neue Socket muss dem Raum erneut beitreten.
        socket.on('connect', () => {
            everConnectedRef.current = true;
            setConnectedBoth(true);
            setReconnecting(false);
            setReconnectFailed(false);
            setError(null);
            socket.emit('playtest:session:join', { sessionId: sessionIdNum });
        });

        // `io client disconnect` ist das eigene Trennen (Verlassen, Anmeldung abgelaufen): dann
        // versucht Socket.io nichts mehr, und es gibt nichts anzuzeigen.
        socket.on('disconnect', (reason) => {
            setConnectedBoth(false);
            if (reason !== 'io client disconnect') setReconnecting(true);
        });

        socket.io?.on('reconnect_failed', () => {
            setReconnecting(false);
            setReconnectFailed(true);
        });

        // Handshake abgelehnt (kein oder entwertetes Token, Prüfung nicht möglich): Socket.io
        // liefert den Umschlag in err.data; ohne diesen Handler bliebe die Ablehnung unsichtbar.
        socket.on('connect_error', (err) => {
            const data = err?.data || {};
            if (data.code === 'UNAUTHENTICATED') {
                // Erst trennen, dann melden: mit einem entwerteten Token laufen sonst alle
                // Reconnect-Versuche gegen dieselbe Ablehnung, während der Nutzer schon
                // weitergeleitet wird. Das Modal von `boardClosed` erklärt; ein Toast daneben wäre doppelt.
                socket.disconnect();
                setReconnecting(false);
                dispatch({ type: 'BOARD_CLOSED', payload: { message: 'Bitte erneut anmelden.', code: 'UNAUTHENTICATED' } });
                return;
            }
            if (everConnectedRef.current) return;
            const clientErr = new ApiClientError({ status: 503, code: data.code ?? null, message: data.message ?? '' });
            setError(errorMessage(clientErr, 'Verbindung zum Playtest fehlgeschlagen.'));
        });

        socket.on('playtest:state:synced', (syncedState) => {
            setSyncedOnce(true);
            dispatch({ type: 'STATE_SYNCED', payload: syncedState });
        });

        const resync = () => socket.emit('playtest:session:reconnect', { sessionId: sessionIdNum });

        for (const [event, toActions] of Object.entries(SERVER_EVENTS)) {
            socket.on(event, (data = {}) => {
                const before = stateRef.current;
                if (event === 'playtest:card:drawn' && !Array.isArray(data.cardsData) && sameUser(data.userId, before?.currentUserId)) {
                    resync();
                    return;
                }
                const actions = [].concat(toActions(data) ?? []);
                if (data.requestId) {
                    dispatch({ type: 'OPTIMISTIC_CONFIRM', payload: { requestId: data.requestId, actions } });
                } else {
                    for (const action of actions) dispatch(action);
                }
                if (RESYNC_AFTER_EVENTS.has(event)) resync();
                const message = EVENT_TOASTS[event]?.(data, before);
                if (message) toast.push({ tone: 'info', message });
            });
        }

        for (const event of ['playtest:hand:viewDenied', 'playtest:card:controlDenied']) {
            socket.on(event, (data = {}) => toast.push({ tone: 'info', message: EVENT_TOASTS[event](data, stateRef.current) }));
        }

        const pingTimers = new Set();
        socket.on('playtest:card:pinged', ({ userId, instanceId, cardName } = {}) => {
            const at = Date.now();
            dispatch({ type: 'PING_RECEIVED', payload: { userId, instanceId, cardName, at } });
            const timer = setTimeout(() => {
                pingTimers.delete(timer);
                dispatch({ type: 'PING_EXPIRED', payload: { instanceId, at } });
            }, PING_VISIBLE_MS);
            pingTimers.add(timer);

            const current = stateRef.current;
            if (sameUser(userId, current?.currentUserId)) return;
            const name = nameOf(current, userId);
            const focus = onFocusCardRef.current;
            toast.push({
                tone: 'info',
                message: `${name} zeigt auf ${cardName}`,
                ...(focus && { onMessageClick: () => focus(instanceId) }),
            });
        });

        // Derselbe Umschlag wie bei HTTP, dazu das auslösende Ereignis und ggf. die requestId.
        socket.on('playtest:error', ({ code, message, requestId } = {}) => {
            const err = new ApiClientError({ status: code === 'INTERNAL' ? 500 : 400, code, message });
            const text = errorMessage(err, 'Aktion im Playtest fehlgeschlagen.');
            if (requestId) dispatch({ type: 'OPTIMISTIC_REVERT', payload: { requestId } });
            if (FATAL_ERROR_CODES.has(code)) {
                dispatch({ type: 'BOARD_CLOSED', payload: { message: text, code } });
                return;
            }
            toast.push({ tone: 'error', message: text });
        });

        return () => {
            for (const timer of pingTimers) clearTimeout(timer);
            socket.disconnect();
            socketRef.current = null;
            connectedRef.current = false;
        };
        // dispatch ist aus useReducer referenz-stabil, toast laut ToastProvider ebenfalls.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, sessionIdNum]);

    // Unbeantwortete Vormerkungen: einmal je Vormerkung den vollen Zustand anfordern.
    useEffect(() => {
        if (!connected) return undefined;
        const timer = setInterval(() => {
            const pending = stateRef.current?.pending || {};
            const handled = resyncedRequestIds.current;
            for (const id of handled) {
                if (!pending[id]) handled.delete(id);
            }
            const now = Date.now();
            const stale = Object.keys(pending).filter(id => !handled.has(id) && now - pending[id].createdAt > PENDING_TIMEOUT_MS);
            if (stale.length === 0) return;
            stale.forEach(id => handled.add(id));
            socketRef.current?.emit('playtest:session:reconnect', { sessionId: sessionIdNum });
        }, PENDING_CHECK_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [connected, sessionIdNum]);

    // ============================================================
    // SENDEN
    // ============================================================

    /**
     * Sendet ein Ereignis; mit `optimistic: { action, inverse }` als Vormerkung mit `requestId`.
     * Ohne Verbindung geht nichts raus und es wird auch nichts vorgemerkt.
     */
    const emit = useCallback((event, payload = {}, { optimistic } = {}) => {
        const socket = socketRef.current;
        if (!socket || !connectedRef.current) return;
        const message = { sessionId: sessionIdNum, ...payload };
        if (optimistic) {
            const requestId = createRequestId();
            dispatch({ type: 'OPTIMISTIC_APPLY', payload: { requestId, action: optimistic.action, inverse: optimistic.inverse, createdAt: Date.now() } });
            message.requestId = requestId;
        }
        socket.emit(event, message);
    }, [sessionIdNum, dispatch]);

    /** Eigener Teilnehmer und eine seiner Karten im aktuellen Zustand. */
    const ownCard = useCallback((instanceId, zones) => {
        const me = stateRef.current?.currentUserId;
        return { me, found: findIn(playerOf(stateRef.current, me), zones, instanceId) };
    }, []);

    // ---------------- Karten ----------------

    const drawCard = useCallback((count = 1, faceDown = false) => {
        emit('playtest:card:draw', { count, faceDown });
    }, [emit]);

    const playCard = useCallback((instanceId, position) => {
        const { me, found } = ownCard(instanceId, ['hand']);
        const optimistic = found && {
            action: { type: 'CARD_PLAYED', payload: { playerId: me, instanceId, card: { ...found.card, position, tapped: false, zIndex: nextZIndex(stateRef.current) } } },
            inverse: { type: 'CARD_MOVED', payload: { playerId: me, instanceId, fromZone: 'battlefield', toZone: 'hand', card: found.card, index: found.index, isUndo: true } },
        };
        emit('playtest:card:play', { instanceId, position }, { optimistic });
    }, [emit, ownCard]);

    /** Eine Anfrage je Karte, jede mit eigener requestId; ein leeres Array sendet nichts. */
    const moveCardZone = useCallback(({ instanceId, fromZone, toZone, position, insertAtTop }) => {
        const ids = Array.isArray(instanceId) ? instanceId : [instanceId];
        for (const id of ids) {
            const { me, found } = ownCard(id, [fromZone]);
            let optimistic;
            if (found) {
                const { card, index } = found;
                const leavesBattlefield = fromZone === 'battlefield' && toZone !== 'battlefield';
                // Eine fremd kontrollierte Karte geht beim Verlassen des Bretts an ihren Besitzer zurück.
                const toOwner = leavesBattlefield && card.controllerId && card.ownerId && !sameUser(card.controllerId, card.ownerId);
                const targetPlayerId = toOwner ? card.ownerId : me;
                const moved = {
                    ...card,
                    ...(toZone === 'battlefield' && { position, tapped: false, zIndex: nextZIndex(stateRef.current) }),
                    ...(leavesBattlefield && { counters: {}, markers: {} }),
                    ...(['graveyard', 'exile', 'deck'].includes(toZone) && { faceDown: false }),
                    ...(toOwner && { controllerId: undefined, faceDown: false }),
                };
                optimistic = {
                    action: { type: 'CARD_MOVED', payload: { playerId: me, targetPlayerId, instanceId: id, fromZone, toZone, card: moved, insertAtTop } },
                    inverse: { type: 'CARD_MOVED', payload: { playerId: targetPlayerId, targetPlayerId: me, instanceId: id, fromZone: toZone, toZone: fromZone, card, index, isUndo: true } },
                };
            }
            emit('playtest:card:moveZone', { instanceId: id, fromZone, toZone, position, insertAtTop }, { optimistic });
        }
    }, [emit, ownCard]);

    const tapCard = useCallback((instanceId) => {
        const { me, found } = ownCard(instanceId, ['battlefield']);
        const tapped = !!found?.card.tapped;
        const tapAction = (value) => ({ type: 'CARD_TAPPED', payload: { playerId: me, instanceId, tapped: value } });
        emit('playtest:card:tap', { instanceId }, { optimistic: found && { action: tapAction(!tapped), inverse: tapAction(tapped) } });
    }, [emit, ownCard]);

    const untapAll = useCallback(() => {
        emit('playtest:card:untapAll');
    }, [emit]);

    const updateCardPosition = useCallback((instanceId, position, zIndex) => {
        const { me, found } = ownCard(instanceId, ['battlefield']);
        const positionAction = (pos, z) => ({ type: 'CARD_POSITION_UPDATED', payload: { playerId: me, instanceId, position: pos, zIndex: z } });
        const optimistic = found && {
            action: positionAction(position, zIndex),
            inverse: positionAction(found.card.position, zIndex === undefined ? undefined : found.card.zIndex ?? 0),
        };
        emit('playtest:card:updatePosition', { instanceId, position, zIndex }, { optimistic });
    }, [emit, ownCard]);

    /** Hebt eine eigene Brettkarte über alle anderen Karten des Bretts. */
    const bringToFront = useCallback((instanceId) => {
        const { me, found } = ownCard(instanceId, ['battlefield']);
        if (!found) return;
        const zIndexes = (stateRef.current?.players || [])
            .flatMap(p => p.battlefield || [])
            .filter(c => c && c.instanceId !== instanceId)
            .map(c => c.zIndex ?? 0);
        const top = Math.max(0, ...zIndexes);
        const current = found.card.zIndex ?? 0;
        if (current > top) return;
        const zIndexAction = (z) => ({ type: 'CARD_Z_INDEX_CHANGED', payload: { playerId: me, instanceId, zIndex: z } });
        emit('playtest:card:updatePosition', { instanceId, position: found.card.position, zIndex: top + 1 }, {
            optimistic: { action: zIndexAction(top + 1), inverse: zIndexAction(current) },
        });
    }, [emit, ownCard]);

    const changeCardCounter = useCallback((event, instanceId, counterType, delta) => {
        const { me, found } = ownCard(instanceId, ['battlefield', 'graveyard', 'exile']);
        const current = found?.card.counters?.[counterType] || 0;
        const applied = Math.max(-current, delta);
        const counterAction = (d) => ({ type: 'COUNTERS_UPDATED', payload: { playerId: me, instanceId, counterType, delta: d } });
        const optimistic = found && applied !== 0 && { action: counterAction(applied), inverse: counterAction(-applied) };
        emit(event, { instanceId, counterType, amount: Math.abs(delta) }, { optimistic });
    }, [emit, ownCard]);

    const addCounter = useCallback((instanceId, counterType, amount = 1) => {
        changeCardCounter('playtest:card:addCounter', instanceId, counterType, amount);
    }, [changeCardCounter]);

    const removeCounter = useCallback((instanceId, counterType, amount = 1) => {
        changeCardCounter('playtest:card:removeCounter', instanceId, counterType, -amount);
    }, [changeCardCounter]);

    const markerAction = useCallback((me, instanceId, markerType, enabled, markers) => (
        { type: 'MARKERS_UPDATED', payload: { playerId: me, instanceId, markerType, enabled, markers } }
    ), []);

    const addMarker = useCallback((instanceId, markerType) => {
        const { me, found } = ownCard(instanceId, ['battlefield', 'graveyard', 'exile']);
        const optimistic = found && {
            action: markerAction(me, instanceId, markerType, true),
            inverse: markerAction(me, instanceId, markerType, !!found.card.markers?.[markerType]),
        };
        emit('playtest:card:addMarker', { instanceId, markerType }, { optimistic });
    }, [emit, ownCard, markerAction]);

    /** `markerType === 'all'` entfernt alle Marker der Karte. */
    const removeMarker = useCallback((instanceId, markerType) => {
        const { me, found } = ownCard(instanceId, ['battlefield', 'graveyard', 'exile']);
        let optimistic;
        if (found && markerType === 'all') {
            optimistic = {
                action: markerAction(me, instanceId, 'all', false, {}),
                inverse: markerAction(me, instanceId, 'all', false, found.card.markers || {}),
            };
        } else if (found) {
            optimistic = {
                action: markerAction(me, instanceId, markerType, false),
                inverse: markerAction(me, instanceId, markerType, !!found.card.markers?.[markerType]),
            };
        }
        emit('playtest:card:removeMarker', { instanceId, markerType }, { optimistic });
    }, [emit, ownCard, markerAction]);

    const createToken = useCallback((tokenCardId, position) => {
        emit('playtest:token:create', { tokenCardId, position });
    }, [emit]);

    const createCardCopy = useCallback((instanceId, position) => {
        emit('playtest:card:copy', { sourceInstanceId: instanceId, position });
    }, [emit]);

    const addTokenToAvailable = useCallback((tokenData) => {
        emit('playtest:token:addToAvailable', { tokenData });
    }, [emit]);

    const shuffleDeck = useCallback(() => {
        emit('playtest:zone:shuffle', { zone: 'deck' });
    }, [emit]);

    /** @param {string} landType - 'Plains'|'Island'|'Swamp'|'Mountain'|'Forest'|'Wastes' */
    const fetchBasicLand = useCallback((landType) => {
        emit('playtest:deck:fetchBasicLand', { landType });
    }, [emit]);

    const transformCard = useCallback((instanceId) => {
        const { me, found } = ownCard(instanceId, ['battlefield', 'hand']);
        const face = found?.card.transformedFace === 1 ? 1 : 0;
        const faceAction = (value) => ({ type: 'CARD_TRANSFORMED', payload: { playerId: me, instanceId, transformedFace: value } });
        emit('playtest:card:transform', { instanceId }, { optimistic: found && { action: faceAction(face === 1 ? 0 : 1), inverse: faceAction(face) } });
    }, [emit, ownCard]);

    const setFaceDown = useCallback((instanceId, faceDown) => {
        const { me, found } = ownCard(instanceId, ['hand', 'battlefield']);
        const faceDownAction = (value) => ({ type: 'CARD_FACEDOWN_CHANGED', payload: { playerId: me, instanceId, faceDown: value } });
        const optimistic = found && { action: faceDownAction(faceDown), inverse: faceDownAction(!!found.card.faceDown) };
        emit('playtest:card:setFaceDown', { instanceId, faceDown }, { optimistic });
    }, [emit, ownCard]);

    /** Zeigt allen kurz einen Ring um eine Brettkarte; ohne Vormerkung, der Ring kommt mit dem Server-Ereignis. */
    const pingCard = useCallback((instanceId) => {
        emit('playtest:card:ping', { instanceId });
    }, [emit]);

    const toggleDeckOpenPlay = useCallback(() => {
        emit('playtest:deck:toggleOpenPlay');
    }, [emit]);

    // ---------------- Teilnehmer ----------------

    const updateLife = useCallback((delta) => {
        const me = stateRef.current?.currentUserId;
        const lifeAction = (d) => ({ type: 'LIFE_UPDATED', payload: { playerId: me, delta: d } });
        emit('playtest:player:updateLife', { delta }, { optimistic: { action: lifeAction(delta), inverse: lifeAction(-delta) } });
    }, [emit]);

    /** Schaden, den der Commander von `sourceUserId` dem eigenen Teilnehmer zufügt. */
    const updateCommanderDamage = useCallback((sourceUserId, delta) => {
        const me = stateRef.current?.currentUserId;
        const damageAction = (d) => ({ type: 'COMMANDER_DAMAGE_UPDATED', payload: { targetUserId: me, sourceUserId, delta: d } });
        emit('playtest:player:updateCommanderDamage', { sourceUserId, delta }, {
            optimistic: { action: damageAction(delta), inverse: damageAction(-delta) },
        });
    }, [emit]);

    /** Ändert eine Marke um `delta` oder setzt sie auf `value`; fehlt sie, entsteht sie (eigene mit `name`/`icon`). */
    const updatePlayerCounter = useCallback(({ counterId, delta, value, name, icon }) => {
        const me = stateRef.current?.currentUserId;
        const existing = (playerOf(stateRef.current, me)?.counters || []).find(c => c.id === counterId);
        const adjust = (change) => ({ type: 'PLAYER_COUNTER_ADJUSTED', payload: { userId: me, counterId, ...change } });
        let inverse;
        if (!existing) {
            inverse = { type: 'PLAYER_COUNTER_REMOVED', payload: { userId: me, counterId } };
        } else if (value !== undefined) {
            inverse = adjust({ value: existing.value });
        } else {
            inverse = adjust({ delta: existing.value - Math.max(0, existing.value + (delta ?? 0)) });
        }
        emit('playtest:player:updateCounter', { counterId, delta, value, name, icon }, {
            optimistic: { action: adjust({ delta, value, name, icon }), inverse },
        });
    }, [emit]);

    const removePlayerCounter = useCallback((counterId) => {
        const me = stateRef.current?.currentUserId;
        const existing = (playerOf(stateRef.current, me)?.counters || []).find(c => c.id === counterId);
        const optimistic = existing && {
            action: { type: 'PLAYER_COUNTER_REMOVED', payload: { userId: me, counterId } },
            inverse: { type: 'PLAYER_COUNTER_ADJUSTED', payload: { userId: me, counterId, value: existing.value, name: existing.name, icon: existing.icon } },
        };
        emit('playtest:player:removeCounter', { counterId }, { optimistic });
    }, [emit]);

    /** @param {number|'coin'} sides - Seitenzahl des Würfels oder 'coin' für einen Münzwurf */
    const rollDice = useCallback((sides) => {
        emit('playtest:dice:roll', { sides });
    }, [emit]);

    const requestViewHand = useCallback((targetUserId) => {
        emit('playtest:hand:requestView', { targetUserId });
    }, [emit]);

    const respondViewHand = useCallback((requesterId, accepted) => {
        emit('playtest:hand:viewResponse', { requesterId, accepted });
    }, [emit]);

    const requestCardControl = useCallback((targetUserId, instanceId) => {
        emit('playtest:card:requestControl', { targetUserId, instanceId });
    }, [emit]);

    const respondCardControl = useCallback((requesterId, instanceId, accepted) => {
        emit('playtest:card:controlResponse', { requesterId, instanceId, accepted });
    }, [emit]);

    const returnCardControl = useCallback((instanceId) => {
        emit('playtest:card:returnControl', { instanceId });
    }, [emit]);

    // ---------------- Mulligan, Zug, Session ----------------

    const takeMulligan = useCallback(() => {
        emit('playtest:mulligan:take');
    }, [emit]);

    /** @param {string[]} cardsToBottom - instanceIds, die unter die Bibliothek gehen (frei wählbar) */
    const keepHand = useCallback((cardsToBottom = []) => {
        emit('playtest:mulligan:keep', { cardsToBottom });
    }, [emit]);

    const endTurn = useCallback(() => {
        emit('playtest:turn:end');
    }, [emit]);

    /** „Ich bin raus": scheidet aus und sieht weiter zu. */
    const eliminate = useCallback(() => {
        emit('playtest:player:eliminate');
    }, [emit]);

    /** „Verlassen": trennt nur die Verbindung; der Server führt den Teilnehmer als abwesend. */
    const leave = useCallback(() => {
        socketRef.current?.disconnect();
    }, []);

    /** Setzt den Playtest für alle Teilnehmer zurück (nur Host). */
    const resetPlaytest = useCallback(() => {
        emit('playtest:player:reset');
    }, [emit]);

    /** Beendet die Session für alle (nur Host). */
    const endSession = useCallback(() => {
        emit('playtest:session:end');
    }, [emit]);

    const requestStateSync = useCallback(() => {
        emit('playtest:session:reconnect');
    }, [emit]);

    /** Neuer Anlauf, nachdem Socket.io alle Wiederverbindungs-Versuche aufgegeben hat. */
    const reconnect = useCallback(() => {
        const socket = socketRef.current;
        if (!socket || connectedRef.current) return;
        setReconnectFailed(false);
        setReconnecting(true);
        socket.connect();
    }, []);

    return {
        connected, reconnecting, reconnectFailed, reconnect, error, syncedOnce,
        drawCard, playCard, moveCardZone,
        tapCard, untapAll, updateCardPosition, bringToFront,
        addCounter, removeCounter, addMarker, removeMarker,
        createToken, createCardCopy, addTokenToAvailable, shuffleDeck, fetchBasicLand,
        transformCard, setFaceDown, pingCard, toggleDeckOpenPlay,
        updateLife, updateCommanderDamage, updatePlayerCounter, removePlayerCounter,
        rollDice, requestViewHand, respondViewHand,
        requestCardControl, respondCardControl, returnCardControl,
        takeMulligan, keepHand, endTurn, eliminate, leave, resetPlaytest, endSession, requestStateSync,
    };
}
